import { useEffect, useRef, useState } from "react";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { ChatMessage } from "@/components/chat/ChatMessage";
import { EmbeddedChatHeader } from "@/components/chat/EmbeddedChatHeader";
import { askChat, startChat } from "@/lib/api";
import "@/pages/Chatbot.css";

const NETWORK_ERROR = "I could not reach the knowledge base right now. Please try again.";
const AUTH_ERROR = "Authentication error, please contact the administrator.";

const makeMessage = (role, content, details = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  role,
  content,
  ...details,
});

const errorText = (error) => (error?.response?.status === 401 ? AUTH_ERROR : NETWORK_ERROR);

export default function Chatbot({ embedded = false, onClose, closeButtonRef }) {
  const [messages, setMessages] = useState([]);
  const [choices, setChoices] = useState([]);
  const [input, setInput] = useState("");
  const [status, setStatus] = useState("connecting");
  const [isStarting, setIsStarting] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const started = useRef(false);
  const mounted = useRef(false);
  const endRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    if (!embedded) document.title = "SuppleAI Perimenopause Chatbot | Supple";
    if (!started.current) {
      started.current = true;

      const openConversation = async () => {
        try {
          const data = await startChat();
          const initialMessages = Array.isArray(data?.messages)
            ? data.messages.filter((item) => typeof item === "string")
            : [];
          if (initialMessages.length === 0) throw new Error("Empty conversation");
          if (mounted.current) {
            setMessages(initialMessages.map((content) => makeMessage("bot", content)));
            setChoices(Array.isArray(data?.choices) ? data.choices : []);
            setStatus("connected");
          }
        } catch (error) {
          if (mounted.current) {
            setMessages([makeMessage("bot", errorText(error), { isError: true })]);
            setStatus("unavailable");
          }
        } finally {
          if (mounted.current) setIsStarting(false);
        }
      };

      openConversation();
    }
    return () => {
      mounted.current = false;
    };
  }, [embedded, mounted, setChoices, setIsStarting, setMessages, setStatus]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, choices, isSending, endRef]);

  const sendQuestion = async (question) => {
    if (!question.trim() || isStarting || isSending || status !== "connected") return;

    setMessages((current) => [...current, makeMessage("user", question)]);
    setInput("");
    setChoices([]);
    setIsSending(true);

    try {
      const data = await askChat(question);
      if (typeof data?.answer !== "string" || !data.answer.trim()) {
        throw new Error("Empty answer");
      }
      setMessages((current) => [
        ...current,
        makeMessage("bot", data.answer, {
          sources: Array.isArray(data.sources) ? data.sources : [],
          redFlag: data.red_flag === true,
        }),
      ]);
      setChoices(Array.isArray(data.choices) ? data.choices : []);
    } catch (error) {
      setMessages((current) => [
        ...current,
        makeMessage("bot", errorText(error), { isError: true }),
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendQuestion(input);
  };

  return (
    <main className={`chatbot-page ${embedded ? "chatbot-page--embedded" : ""}`} data-testid={embedded ? "floating-chatbot-page" : "chatbot-page"}>
      {embedded ? <EmbeddedChatHeader status={status} onClose={onClose} closeButtonRef={closeButtonRef} /> : <ChatHeader status={status} />}

      <section className="chat-shell" aria-label="SuppleAI conversation">
        <div
          className="chat-thread"
          aria-live="polite"
          aria-busy={isStarting || isSending}
          data-testid="chat-messages-list"
        >
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}

          {(isStarting || isSending) && (
            <div className="chat-message chat-message--bot" data-testid="chat-loading-indicator">
              <span className="chat-message__avatar chat-message__avatar--loading" aria-hidden="true" />
              <div className="chat-typing" aria-label="Loading response">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {choices.length > 0 && !isSending && (
            <div className="chat-choices" data-testid="chat-choice-list">
              {choices.map((choice, index) => (
                <button
                  type="button"
                  key={`${choice}-${index}`}
                  onClick={() => sendQuestion(choice)}
                  data-testid={`choice-button-${index}`}
                >
                  {choice}
                </button>
              ))}
            </div>
          )}
          <div ref={endRef} aria-hidden="true" />
        </div>

        <ChatComposer
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          disabled={isStarting || isSending || status !== "connected"}
        />
      </section>
    </main>
  );
}